// This package provides transformer functions that transform JavaScript objects
// into JSON ready objects that can be passed to RPC. It following the following
// rules:
//
// 1. If the specified object has a serializeJson method, it would invoke this
// method and use the result to replace current object.
// 2. It then restricts the keys of the object to keys required by the specified
// entity(i.e., a Script would only have code_hash, hash_type, args keys),for each
// sub-field, it then recursively perform the steps here from step 1.
// 3. It then optionally run validator functions to ensure the resulting object
// follows specified rules.
//
// Note rule 1 here provides the flexibility in defining your own structures: you
// could define a class containing custom data structures, then provide a
// serializeJson that transforms the custom one into the rigid data structure
// required by CKB. You can also leverage the Reader class we provide as much as
// possible. Since Reader class does provide serializeJson methods, transformers
// here will transform them to valid hex strings required by CKB.
import * as validators from './validators';
function invokeSerializeJson(debugPath, value) {
    if (value instanceof Object && value.serializeJson instanceof Function) {
        return value.serializeJson.call(value);
    }
    return value;
}
function transformObject(debugPath, object, keys) {
    object = invokeSerializeJson(debugPath, object);
    if (!(object instanceof Object)) {
        throw new Error(`Transformed ${debugPath} is not an object!`);
    }
    const result = {};
    for (const [key, f] of Object.entries(keys)) {
        let value = object[key];
        if (!value) {
            const camelKey = key.replace(/(_[a-z])/g, (group) => group.toUpperCase().replace('_', ''));
            value = object[camelKey];
        }
        if (value) {
            result[key] = f(`${debugPath}.${key}`, value);
        }
    }
    return result;
}
export function TransformScript(script, { validation = true, debugPath = 'script' } = {}) {
    script = transformObject(debugPath, script, {
        code_hash: invokeSerializeJson,
        hash_type: invokeSerializeJson,
        args: invokeSerializeJson,
    });
    if (validation) {
        validators.ValidateScript(script, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return script;
}
export function TransformOutPoint(outPoint, { validation = true, debugPath = 'out_point' } = {}) {
    outPoint = transformObject(debugPath, outPoint, {
        tx_hash: invokeSerializeJson,
        index: invokeSerializeJson,
    });
    if (validation) {
        validators.ValidateOutPoint(outPoint, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return outPoint;
}
function toInvoke(transform) {
    return function (debugPath, value) {
        return transform(value, {
            validation: false,
            debugPath,
        });
    };
}
export function TransformCellInput(cellInput, { validation = true, debugPath = 'cell_input' } = {}) {
    cellInput = transformObject(debugPath, cellInput, {
        since: invokeSerializeJson,
        previous_output: toInvoke(TransformOutPoint),
    });
    if (validation) {
        validators.ValidateCellInput(cellInput, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return cellInput;
}
export function TransformCellOutput(cellOutput, { validation = true, debugPath = 'cell_output' } = {}) {
    cellOutput = transformObject(debugPath, cellOutput, {
        capacity: invokeSerializeJson,
        lock: toInvoke(TransformScript),
        type: toInvoke(TransformScript),
    });
    if (validation) {
        validators.ValidateCellOutput(cellOutput, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return cellOutput;
}
export function TransformCellDep(cellDep, { validation = true, debugPath = 'cell_dep' } = {}) {
    cellDep = transformObject(debugPath, cellDep, {
        out_point: toInvoke(TransformOutPoint),
        dep_type: invokeSerializeJson,
    });
    if (validation) {
        validators.ValidateCellDep(cellDep, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return cellDep;
}
function toInvokeArray(invokeFunction) {
    return function (debugPath, array) {
        return array.map((item, i) => {
            return invokeFunction(`${debugPath}[${i}]`, item);
        });
    };
}
export function TransformRawTransaction(rawTransaction, { validation = true, debugPath = 'raw_transaction' } = {}) {
    rawTransaction = transformObject(debugPath, rawTransaction, {
        version: invokeSerializeJson,
        cell_deps: toInvokeArray(toInvoke(TransformCellDep)),
        header_deps: toInvokeArray(invokeSerializeJson),
        inputs: toInvokeArray(toInvoke(TransformCellInput)),
        outputs: toInvokeArray(toInvoke(TransformCellOutput)),
        outputs_data: toInvokeArray(invokeSerializeJson),
    });
    if (validation) {
        validators.ValidateRawTransaction(rawTransaction, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return rawTransaction;
}
export function TransformTransaction(transaction, { validation = true, debugPath = 'transaction' } = {}) {
    transaction = transformObject(debugPath, transaction, {
        version: invokeSerializeJson,
        cell_deps: toInvokeArray(toInvoke(TransformCellDep)),
        header_deps: toInvokeArray(invokeSerializeJson),
        inputs: toInvokeArray(toInvoke(TransformCellInput)),
        outputs: toInvokeArray(toInvoke(TransformCellOutput)),
        outputs_data: toInvokeArray(invokeSerializeJson),
        witnesses: toInvokeArray(invokeSerializeJson),
    });
    if (validation) {
        validators.ValidateTransaction(transaction, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return transaction;
}
export function TransformRawHeader(rawHeader, { validation = true, debugPath = 'raw_header' } = {}) {
    rawHeader = transformObject(debugPath, rawHeader, {
        version: invokeSerializeJson,
        compact_target: invokeSerializeJson,
        timestamp: invokeSerializeJson,
        number: invokeSerializeJson,
        epoch: invokeSerializeJson,
        parent_hash: invokeSerializeJson,
        transactions_root: invokeSerializeJson,
        proposals_hash: invokeSerializeJson,
        uncles_hash: invokeSerializeJson,
        dao: invokeSerializeJson,
    });
    if (validation) {
        validators.ValidateRawHeader(rawHeader, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return rawHeader;
}
export function TransformHeader(header, { validation = true, debugPath = 'header' } = {}) {
    header = transformObject(debugPath, header, {
        version: invokeSerializeJson,
        compact_target: invokeSerializeJson,
        timestamp: invokeSerializeJson,
        number: invokeSerializeJson,
        epoch: invokeSerializeJson,
        parent_hash: invokeSerializeJson,
        transactions_root: invokeSerializeJson,
        proposals_hash: invokeSerializeJson,
        uncles_hash: invokeSerializeJson,
        dao: invokeSerializeJson,
        nonce: invokeSerializeJson,
    });
    if (validation) {
        validators.ValidateHeader(header, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return header;
}
export function TransformUncleBlock(uncleBlock, { validation = true, debugPath = 'uncle_block' } = {}) {
    uncleBlock = transformObject(debugPath, uncleBlock, {
        header: toInvoke(TransformHeader),
        proposals: toInvokeArray(invokeSerializeJson),
    });
    if (validation) {
        validators.ValidateUncleBlock(uncleBlock, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return uncleBlock;
}
export function TransformBlock(block, { validation = true, debugPath = 'block' } = {}) {
    block = transformObject(debugPath, block, {
        header: toInvoke(TransformHeader),
        uncles: toInvokeArray(toInvoke(TransformUncleBlock)),
        transactions: toInvokeArray(toInvoke(TransformTransaction)),
        proposals: toInvokeArray(invokeSerializeJson),
    });
    if (validation) {
        validators.ValidateBlock(block, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return block;
}
export function TransformCellbaseWitness(cellbaseWitness, { validation = true, debugPath = 'cellbase_witness' } = {}) {
    cellbaseWitness = transformObject(debugPath, cellbaseWitness, {
        lock: toInvoke(TransformScript),
        message: invokeSerializeJson,
    });
    if (validation) {
        validators.ValidateCellbaseWitness(cellbaseWitness, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return cellbaseWitness;
}
export function TransformWitnessArgs(witnessArgs, { validation = true, debugPath = 'witness_args' } = {}) {
    witnessArgs = transformObject(debugPath, witnessArgs, {
        lock: invokeSerializeJson,
        input_type: invokeSerializeJson,
        output_type: invokeSerializeJson,
    });
    if (validation) {
        validators.ValidateWitnessArgs(witnessArgs, {
            debugPath: `(transformed) ${debugPath}`,
        });
    }
    return witnessArgs;
}
export const transformers = {
    TransformScript,
    TransformOutPoint,
    TransformCellInput,
    TransformCellOutput,
    TransformCellDep,
    TransformRawTransaction,
    TransformTransaction,
    TransformRawHeader,
    TransformHeader,
    TransformUncleBlock,
    TransformBlock,
    TransformCellbaseWitness,
    TransformWitnessArgs,
};
//# sourceMappingURL=data:application/json;base64,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